/* Запросы экрана смарт-предложений. Все ключи — через locKey: SSE-изменения точки перезапросят их сами. */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { Offer } from "@okno/shared";
import { api } from "../../lib/api";
import { locKey, useSession } from "../../lib/session";
import type { OfferContext, OfferDraft, OfferPreview } from "../../lib/types";

/** Глубина списка кампаний на экране. */
export const LIST_DAYS = 60;
const PREVIEW_DEBOUNCE_MS = 300;

export function useOfferContext() {
  const { locationId } = useSession();
  return useQuery({
    queryKey: locKey(locationId, "offers", "context"),
    queryFn: () => api.get<OfferContext>("/locations/" + locationId + "/offers/context"),
  });
}

export function useOffersList(days = LIST_DAYS, enabled = true) {
  const { locationId } = useSession();
  return useQuery({
    queryKey: locKey(locationId, "offers", "list", days),
    queryFn: () => api.get<Offer[]>("/locations/" + locationId + "/offers", { days }),
    enabled,
  });
}

/** Значение, которое меняется не чаще, чем раз в ms после последнего изменения. */
function useDebounced(value: string | null, ms: number): string | null {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Предпросмотр аудитории и экономики: запрос уходит через 300 мс после последнего изменения черновика. */
export function useOfferPreview(draft: OfferDraft | null) {
  const { locationId } = useSession();
  const key = draft ? JSON.stringify(draft) : null;
  const debounced = useDebounced(key, PREVIEW_DEBOUNCE_MS);
  const q = useQuery({
    queryKey: locKey(locationId, "offers", "preview", debounced),
    queryFn: () => api.post<OfferPreview>("/locations/" + locationId + "/offers/preview", JSON.parse(debounced as string) as OfferDraft),
    enabled: !!debounced,
    placeholderData: (prev) => prev,
    retry: false,
  });
  // неполный черновик — предпросмотра нет, старый не показываем
  return { preview: key ? q.data : undefined, error: key ? q.error : null, pending: key !== debounced || q.isFetching };
}
